/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Search, message rows, and paging for one folder.
 *
 * Rows are their own memoised component at module scope. They used to be
 * declared inside the page component, so every keystroke anywhere re-created
 * the list and reset its scroll position.
 */

import React, { memo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { avatarTone, formatListDate, initialsFor, parseAddress, previewText } from '../emailFormat';

export function EmailListSkeleton({ count }) {
  return Array.from({ length: count }).map((_, index) => (
    <Box key={index} sx={{ display: 'flex', gap: 1.5, px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
      <Skeleton variant="circular" width={36} height={36} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Skeleton variant="text" width="45%" />
          <Skeleton variant="text" width="15%" />
        </Box>
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="65%" />
      </Box>
    </Box>
  ));
}

EmailListSkeleton.propTypes = { count: PropTypes.number };
EmailListSkeleton.defaultProps = { count: 6 };

const EmailRow = memo(function EmailRow({ email, selected, isDraftsFolder, isSentFolder, onSelect, onEditDraft }) {
  // In Sent and Drafts the interesting party is the recipient.
  const showRecipient = isSentFolder || isDraftsFolder;
  const party = showRecipient
    ? parseAddress((email.to || '').split(',')[0])
    : parseAddress(email.from, email.from_name);
  const partyLabel = showRecipient ? (party.name ? `To: ${party.name}` : '(no recipient)') : party.name || '(unknown sender)';
  const tone = avatarTone(party.email || party.name);
  const unread = !email.read && !isDraftsFolder;

  return (
    <Box
      component="li"
      role="option"
      aria-selected={selected}
      tabIndex={0}
      onClick={() => onSelect(email)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(email);
        }
      }}
      sx={(theme) => ({
        listStyle: 'none',
        display: 'flex',
        gap: 1.5,
        px: 2,
        py: 1.5,
        cursor: 'pointer',
        position: 'relative',
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: selected ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.08) : 'transparent',
        '&:hover': { bgcolor: selected ? undefined : theme.palette.action.hover },
        '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
        '&::before': unread
          ? { content: '""', position: 'absolute', left: 0, top: 12, bottom: 12, width: 3, borderRadius: 2, bgcolor: 'primary.main' }
          : undefined,
      })}
    >
      <Avatar sx={{ width: 36, height: 36, fontSize: '0.8125rem', fontWeight: 600, bgcolor: tone.background, color: tone.text }}>
        {initialsFor(party.name || party.email)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="body2" noWrap sx={{ flex: 1, fontWeight: unread ? 700 : 500 }}>
            {partyLabel}
          </Typography>
          <Typography variant="caption" color={unread ? 'primary' : 'text.secondary'} sx={{ flexShrink: 0, fontWeight: unread ? 600 : 400 }}>
            {formatListDate(email.date)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" noWrap sx={{ flex: 1, fontWeight: unread ? 600 : 400 }}>
            {email.subject || '(no subject)'}
          </Typography>
          {email.has_attachments && <AttachFileIcon sx={{ fontSize: 16, color: 'text.secondary', transform: 'rotate(45deg)' }} />}
          {isDraftsFolder && (
            <Tooltip title="Edit draft">
              <IconButton
                size="small"
                aria-label="Edit draft"
                onClick={(event) => {
                  event.stopPropagation();
                  onEditDraft(email);
                }}
                sx={{ p: 0.25 }}
              >
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" component="div" noWrap>
          {previewText(email)}
        </Typography>
      </Box>
    </Box>
  );
});

EmailRow.propTypes = {
  email: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  isDraftsFolder: PropTypes.bool,
  isSentFolder: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onEditDraft: PropTypes.func.isRequired,
};

EmailRow.defaultProps = { selected: false, isDraftsFolder: false, isSentFolder: false };

function EmailListPane({
  title,
  emails,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  selectedId,
  onSelect,
  onEditDraft,
  isDraftsFolder,
  isSentFolder,
  searchQuery,
  onSearchChange,
  onRefresh,
  error,
  onRetry,
  onReconnect,
  headerSlot,
  sx,
}) {
  const sentinelRef = useRef(null);
  const scrollRef = useRef(null);

  // Load the next page when the end of the list scrolls into view; the
  // button below stays as the keyboard/no-IntersectionObserver path.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loading || loadingMore || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadMore();
      },
      { root: scrollRef.current, rootMargin: '120px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, onLoadMore]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, ...sx }}>
      <Box sx={{ px: 2, pt: 1.5, pb: 1.25, borderBottom: 1, borderColor: 'divider' }}>
        {headerSlot}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="subtitle1" component="h2" noWrap sx={{ flex: 1 }}>
            {title}
          </Typography>
          <Tooltip title="Refresh">
            <span>
              <IconButton size="small" onClick={onRefresh} disabled={loading} aria-label="Refresh">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <TextField
          fullWidth
          size="small"
          placeholder="Search mail"
          value={searchQuery}
          onChange={onSearchChange}
          inputProps={{ 'aria-label': 'Search mail' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {error && (
          <Alert
            severity={error.reconnect ? 'warning' : 'error'}
            sx={{ m: 1.5 }}
            action={
              error.reconnect && onReconnect ? (
                <Button color="inherit" size="small" onClick={onReconnect}>
                  Reconnect
                </Button>
              ) : (
                <Button color="inherit" size="small" onClick={onRetry}>
                  Retry
                </Button>
              )
            }
          >
            {error.message}
          </Alert>
        )}

        {loading ? (
          <EmailListSkeleton count={7} />
        ) : !emails.length && !error ? (
          <Box sx={{ py: 8, px: 3, textAlign: 'center', color: 'text.secondary' }}>
            <InboxOutlinedIcon sx={{ fontSize: 40, mb: 1, opacity: 0.6 }} />
            <Typography variant="body2" color="text.secondary">
              {searchQuery ? 'No messages match your search.' : 'No messages in this folder.'}
            </Typography>
          </Box>
        ) : (
          <Box component="ul" role="listbox" aria-label={title} sx={{ m: 0, p: 0 }}>
            {emails.map((email) => (
              <EmailRow
                key={email.id}
                email={email}
                selected={email.id === selectedId}
                isDraftsFolder={isDraftsFolder}
                isSentFolder={isSentFolder}
                onSelect={onSelect}
                onEditDraft={onEditDraft}
              />
            ))}
          </Box>
        )}

        {loadingMore && <EmailListSkeleton count={2} />}

        {!loading && hasMore && (
          <Box ref={sentinelRef} sx={{ p: 1.5 }}>
            <Button fullWidth variant="text" onClick={onLoadMore} disabled={loadingMore}>
              {loadingMore ? <CircularProgress size={20} /> : 'Load more'}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

EmailListPane.propTypes = {
  title: PropTypes.string.isRequired,
  emails: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  loadingMore: PropTypes.bool,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func.isRequired,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onEditDraft: PropTypes.func.isRequired,
  isDraftsFolder: PropTypes.bool,
  isSentFolder: PropTypes.bool,
  searchQuery: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  error: PropTypes.shape({ message: PropTypes.string, reconnect: PropTypes.bool }),
  onRetry: PropTypes.func.isRequired,
  onReconnect: PropTypes.func,
  headerSlot: PropTypes.node,
  sx: PropTypes.object,
};

EmailListPane.defaultProps = {
  loading: false,
  loadingMore: false,
  hasMore: false,
  selectedId: null,
  isDraftsFolder: false,
  isSentFolder: false,
  searchQuery: '',
  error: null,
  onReconnect: null,
  headerSlot: null,
  sx: {},
};

export default EmailListPane;
